/**
 * DrillDownsRail — launcher tile grid for the 5 deep-dive Performance pages.
 *
 * Each tile briefly describes what the drill-down answers and links to its
 * dedicated route. Designed to live on the main Performance dashboard so the
 * drill-downs are discoverable without leaving the page.
 */
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Briefcase,
  Crown,
  Filter,
  Flame,
  Layers,
  Compass,
} from 'lucide-react';
import { clsx } from 'clsx';

interface Drilldown {
  to: string;
  title: string;
  question: string;
  icon: JSX.Element;
  accent: string;
}

const DRILLDOWNS: Drilldown[] = [
  {
    to: '/creative/performance/formulas',
    title: 'Creative Formula Intelligence',
    question: 'Which formulas are steering Meta to the right persona?',
    icon: <Layers size={16} />,
    accent: 'from-teal-50 to-white border-teal-200 text-teal-700',
  },
  {
    to: '/creative/performance/quality',
    title: 'Customer Quality & LTV Cohorts',
    question: 'Which creative attracts your highest-LTV customers?',
    icon: <Crown size={16} />,
    accent: 'from-emerald-50 to-white border-emerald-200 text-emerald-700',
  },
  {
    to: '/creative/performance/fatigue',
    title: 'Creative Fatigue Tracker',
    question: 'Which ads are dying and need refresh — by when?',
    icon: <Flame size={16} />,
    accent: 'from-amber-50 to-white border-amber-200 text-amber-700',
  },
  {
    to: '/creative/performance/campaigns',
    title: 'Campaign Performance & Steering',
    question: 'Which campaigns deliver the persona you briefed for?',
    icon: <Briefcase size={16} />,
    accent: 'from-indigo-50 to-white border-indigo-200 text-indigo-700',
  },
  {
    to: '/creative/performance/funnel',
    title: 'Funnel & Source Attribution',
    question: 'Which sources convert deepest, where does Meta over-index?',
    icon: <Filter size={16} />,
    accent: 'from-violet-50 to-white border-violet-200 text-violet-700',
  },
];

export function DrillDownsRail() {
  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2">
        <Compass size={13} className="text-slate-500" />
        <h2 className="text-sm font-bold text-navy-900">Drill-down Reports</h2>
        <span className="text-[10px] text-slate-400">deep dives wired to the Customer Agent MCP</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
        {DRILLDOWNS.map((d) => (
          <Link
            key={d.to}
            to={d.to}
            className={clsx(
              'group bg-gradient-to-b border rounded-xl p-3.5 space-y-2 hover:shadow-sm transition-all',
              d.accent,
            )}
          >
            <div className="flex items-center justify-between">
              <div className={clsx('rounded-lg p-1.5 bg-white/60 ring-1 ring-current/10')}>
                {d.icon}
              </div>
              <ArrowRight
                size={14}
                className="text-slate-400 group-hover:text-current group-hover:translate-x-0.5 transition-all"
              />
            </div>
            <div className="text-[12px] font-bold text-slate-800 leading-snug">{d.title}</div>
            <p className="text-[10px] text-slate-500 leading-snug">{d.question}</p>
          </Link>
        ))}
      </div>
    </section>
  );
}
